import { useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Coffee, Plus } from "lucide-react";
import { List } from "../../../../components/shared/Form";
import Button from "../../../../components/shared/Button";
import Dialog from "../../../../components/shared/Dialog";
import Break from "../../../TraxxionSchedule/Component/components/drawers/Event/Form/Simple/Breaks/Break";
import AutomaticBreaks from "../../../TraxxionSchedule/Component/components/drawers/Event/Form/Simple/Breaks/AutomaticBreaks";
import { useVisible } from "../../../../utils/hooks/useVisible";
import { cn } from "../../../../lib/utils";

function Breaks({ user, disabled }) {
    const { t } = useTranslation();
    const { formState, getValues, control, trigger } = useFormContext();
    const { open, visible, close } = useVisible();
    const [defaultBreaks, setDefaultBreaks] = useState([]);
    const breaks = useWatch({ control, name: "breaks" });

    const manualBreak = user?.calculationGroup?.breaks?.manual;
    const canAddBreaks = !!manualBreak?.status && !disabled;

    const openDialog = useCallback(() => {
        setDefaultBreaks(getValues("breaks") ?? []);
        open();
    }, [getValues, open]);

    return (
        <>
            <Button
                type="button"
                variant="ghost"
                className={cn("tw-mb-6", formState.errors?.breaks ? "tw-text-rose-500" : "tw-text-primary")}
                onClick={openDialog}
            >
                <Coffee />
                {canAddBreaks ? t("add-break") : t("breaks")}
            </Button>

            <Dialog title={canAddBreaks ? t("add-breaks-to-shift-details") : t("breaks")} open={visible} close={close} size="2xl">
                {canAddBreaks ? (
                    <List name="breaks">
                        {(fields, { add, remove, replace }) => {
                            const cancel = () => {
                                replace(defaultBreaks);
                                close();
                            };

                            const confirm = async () => {
                                if (await trigger("breaks")) {
                                    close();
                                }
                            };

                            return (
                                <>
                                    {fields.length === 0 && (
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-3 tw-mb-2 tw-border-2 tw-rounded-xl tw-border-dashed">
                                            <img src="/assets/tea-cup.png" alt="" width={50} draggable="false" />
                                            <p className="tw-text-sm tw-font-medium tw-text-neutral-700">{t("breaks")}</p>
                                            <p className="tw-text-xs tw-text-neutral-400 tw-text-center">{t("add-breaks-to-shift-details")}</p>
                                        </div>
                                    )}

                                    {fields.length > 0 && (
                                        <div className="tw-rounded-lg tw-border tw-border-neutral-100 tw-bg-neutral-50 tw-px-3 tw-mb-1 tw-divide-y tw-divide-neutral-100">
                                            {fields.map((field, index) => (
                                                <Break
                                                    key={field.id}
                                                    name={`breaks.${index}`}
                                                    index={index}
                                                    remove={() => remove(index)}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    <div className={cn(fields.length > 0 ? "tw-flex tw-justify-end" : "tw-w-full")}>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant={fields.length === 0 ? "outline" : "ghost"}
                                            className={cn(
                                                "tw-text-primary",
                                                fields.length === 0 ? "tw-border-primary tw-w-full" : "tw-w-fit tw-text-xs"
                                            )}
                                            onClick={() => add({ type: "break", start: undefined, end: undefined, payCode: manualBreak?.payCode })}
                                        >
                                            <Plus size={fields.length === 0 ? 13 : 11} /> {t("add-break")}
                                        </Button>
                                    </div>

                                    <div className="tw-flex tw-items-center tw-gap-2 tw-justify-end tw-mt-4 tw-w-full">
                                        <Button variant="ghost" type="button" onClick={cancel}>
                                            {t("cancel")}
                                        </Button>
                                        <Button type="button" onClick={confirm}>
                                            {t("confirm")}
                                        </Button>
                                    </div>
                                </>
                            );
                        }}
                    </List>
                ) : (
                    <>
                        <AutomaticBreaks breaks={breaks ?? []} />
                        <div className="tw-flex tw-justify-end">
                            <Button variant="ghost" type="button" onClick={close}>
                                {t("close")}
                            </Button>
                        </div>
                    </>
                )}
            </Dialog>
        </>
    );
}

export default Breaks;
